import React, { useState, useEffect, useRef, useCallback } from "react";
import "./AiPanel.css";
import {
  fetchAiFarms,
  sendChatMessage,
  sendMapCommand,
  generateReport,
  fetchSessions,
  createSession,
  fetchSessionMessages,
  deleteSession,
} from "../api/cropAiApi";

// Mock agriculture/farm data (used as fallback when no real data is available)
const MOCK_FARMS = [
  {
    id: "farm_001",
    name: "شرکت کشاورزی آبادان",
    owner: "مدیریت زراعی آبادان",
    area_ha: 50.0,
    crop_type: "rice",
    growth_stage: "tillering",
  },
  {
    id: "farm_002",
    name: "شرکت کشت و صنعت امیرکبیر",
    owner: "مدیریت کشت و صنعت امیرکبیر",
    area_ha: 35.0,
    crop_type: "sugarcane",
    growth_stage: "grand_growth",
  },
  {
    id: "farm_003",
    name: "مزرعه گل و گیاه خوزستان",
    owner: "تعاونی کشاورزی خوزستان",
    area_ha: 28.5,
    crop_type: "cotton",
    growth_stage: "flowering",
  },
];

const AiPanel = ({ onBack }) => {
  const [chatHistory, setChatHistory] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [mapMode, setMapMode] = useState(false);
  const [farms, setFarms] = useState([]);
  const [selectedFarmId, setSelectedFarmId] = useState(""); // This is actually agriculture_id from backend
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionsPanelOpen, setSessionsPanelOpen] = useState(false);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory, scrollToBottom]);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const farmsData = await fetchAiFarms();
        const fetchedFarms = farmsData.farms || [];
        // Use mock data as fallback if no real farms/agriculture data available
        setFarms(fetchedFarms.length > 0 ? fetchedFarms : MOCK_FARMS);
      } catch (err) {
        console.error("Failed to load farms:", err);
        // Use mock data on error
        setFarms(MOCK_FARMS);
      }
      try {
        const sessionsData = await fetchSessions();
        setSessions(sessionsData.sessions || []);
        if (sessionsData.sessions && sessionsData.sessions.length > 0) {
          const latestSession = sessionsData.sessions[0];
          setCurrentSessionId(latestSession.id);
          try {
            const msgs = await fetchSessionMessages(latestSession.id);
            setChatHistory(
              (msgs.messages || []).map((m) => ({
                role: m.role,
                content: m.content,
              })),
            );
          } catch (err2) {
            console.error("Failed to load session messages:", err2);
          }
        }
      } catch (err) {
        console.error("Failed to load sessions:", err);
      }
    };
    loadInitialData();
  }, []);

  const handleToggleSessionsPanel = async () => {
    if (!sessionsPanelOpen) {
      setSessionsLoading(true);
      try {
        const data = await fetchSessions();
        setSessions(data.sessions || []);
      } catch (err) {
        console.error("Failed to load sessions:", err);
      } finally {
        setSessionsLoading(false);
      }
    }
    setSessionsPanelOpen(!sessionsPanelOpen);
  };

  const handleStartNewChat = () => {
    setCurrentSessionId(null);
    setChatHistory([]);
    setSessionsPanelOpen(false);
  };

  const handleLoadSession = async (sessionId) => {
    setCurrentSessionId(sessionId);
    setSessionsPanelOpen(false);
    setChatHistory([]);
    try {
      const data = await fetchSessionMessages(sessionId);
      setChatHistory(
        (data.messages || []).map((m) => ({
          role: m.role,
          content: m.content,
        })),
      );
    } catch (err) {
      console.error("Failed to load session:", err);
    }
    setSessions((prev) =>
      prev.map((s) => (s.id === sessionId ? { ...s, _active: true } : s)),
    );
  };

  const handleDeleteSession = async (e, sessionId) => {
    e.stopPropagation();
    try {
      await deleteSession(sessionId);
      if (currentSessionId === sessionId) {
        handleStartNewChat();
      }
      const data = await fetchSessions();
      setSessions(data.sessions || []);
    } catch (err) {
      console.error("Failed to delete session:", err);
    }
  };

  const handleSendMessage = async (e) => {
    if (e) e.preventDefault();
    const query = inputValue.trim();
    if (!query || isLoading) return;

    setInputValue("");

    const userMessage = { role: "user", content: query };
    const loadingMessage = { role: "assistant", content: null, loading: true };
    const newHistory = [...chatHistory, userMessage, loadingMessage];
    setChatHistory(newHistory);
    setIsLoading(true);

    if (mapMode) {
      try {
        const data = await sendMapCommand(query);
        const message = data.message || "عملیات روی نقشه انجام شد.";
        setChatHistory((prev) => {
          const copy = [...prev];
          const lastIdx = copy.length - 1;
          copy[lastIdx] = { role: "assistant", content: message };
          return copy;
        });
      } catch (err) {
        const errorMsg = err.message || "خطا در اتصال به سرور.";
        setChatHistory((prev) => {
          const copy = [...prev];
          const lastIdx = copy.length - 1;
          copy[lastIdx] = {
            role: "assistant",
            content: errorMsg,
            isError: true,
          };
          return copy;
        });
      } finally {
        setIsLoading(false);
      }
    } else {
      const historyForApi = [...chatHistory, userMessage];
      let sessionId = currentSessionId;

      if (!sessionId) {
        try {
          const session = await createSession({
            farmId: selectedFarmId || undefined,
          });
          if (session && session.id) {
            sessionId = session.id;
            setCurrentSessionId(sessionId);
          }
        } catch (err) {
          console.error("Session creation failed:", err);
        }
      }

      try {
        const data = await sendChatMessage({
          messages: historyForApi,
          farmId: selectedFarmId || undefined,
          sessionId: sessionId || undefined,
        });
        setChatHistory((prev) => {
          const copy = [...prev];
          const lastIdx = copy.length - 1;
          copy[lastIdx] = { role: "assistant", content: data.message };
          return copy;
        });
      } catch (err) {
        const errorMsg = err.message || "خطا در اتصال به سرور.";
        setChatHistory((prev) => {
          const copy = [...prev];
          const lastIdx = copy.length - 1;
          copy[lastIdx] = {
            role: "assistant",
            content: errorMsg,
            isError: true,
          };
          return copy;
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleGenerateReport = async (reportType) => {
    const farmId = selectedFarmId;
    if (!farmId) {
      const hintMsg = {
        role: "assistant",
        content: "لطفاً ابتدا یک مزرعه از لیست بالا انتخاب کنید.",
        isError: false,
      };
      setChatHistory((prev) => [...prev, hintMsg]);
      return;
    }
    const farmName = farms.find((f) => f.id === farmId)?.name || "مزرعه";
    const prompts = {
      "گزارش وضعیت مزارع": `لطفاً یک گزارش جامع از وضعیت ${farmName} تهیه کنید.`,
      "گزارش وضعیت گیاه": `لطفاً وضعیت گیاهی ${farmName} را با جزئیات تحلیل کنید.`,
      "گزارش وضعیت خاک": `لطفاً وضعیت خاک و شاخص‌های مرتبط در ${farmName} را بررسی کنید.`,
    };
    const query =
      prompts[reportType] ||
      `لطفاً یک گزارش از ${reportType} برای مزرعه انتخاب‌شده تهیه کنید.`;

    setReportLoading(true);
    const userMessage = { role: "user", content: query };
    const loadingMessage = { role: "assistant", content: null, loading: true };
    setChatHistory((prev) => [...prev, userMessage, loadingMessage]);
    setIsLoading(true);

    try {
      const data = await generateReport(farmId);
      const reportText = data.report || "گزارش تولید نشد.";
      setChatHistory((prev) => {
        const copy = [...prev];
        const lastIdx = copy.length - 1;
        copy[lastIdx] = { role: "assistant", content: reportText };
        return copy;
      });
    } catch (err) {
      const errorMsg = err.message || "خطا در تولید گزارش.";
      setChatHistory((prev) => {
        const copy = [...prev];
        const lastIdx = copy.length - 1;
        copy[lastIdx] = {
          role: "assistant",
          content: errorMsg,
          isError: true,
        };
        return copy;
      });
    } finally {
      setIsLoading(false);
      setReportLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const renderMessageContent = (text) => {
    if (!text) return "";
    const lines = String(text).split("\n");
    return lines.map((line, idx) => (
      <React.Fragment key={idx}>
        {renderInlineMarkdown(line)}
        {idx < lines.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  const renderInlineMarkdown = (line) => {
    const parts = [];
    const regex = /\*\*(.*?)\*\*/g;
    let lastIndex = 0;
    let match;
    let key = 0;
    while ((match = regex.exec(line)) !== null) {
      if (match.index > lastIndex) {
        parts.push(
          <span key={`text-${key++}`}>
            {line.slice(lastIndex, match.index)}
          </span>,
        );
      }
      parts.push(<strong key={`bold-${key++}`}>{match[1]}</strong>);
      lastIndex = regex.lastIndex;
    }
    if (lastIndex < line.length) {
      parts.push(<span key={`text-${key++}`}>{line.slice(lastIndex)}</span>);
    }
    return parts;
  };

  const formatDateFa = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString("fa-IR");
    } catch {
      return "";
    }
  };

  return (
    <div className="ai-panel">
      <div className="ai-panel-back-header">
        <button
          className="ai-back-btn"
          onClick={onBack}
          aria-label="بازگشت به منو"
          title="بازگشت به منو"
        >
          ←
        </button>
        <div className="ai-panel-brand">
          <div className="ai-panel-brand-text">
            <span className="ai-panel-title">فرادید</span>
            <span className="ai-panel-subtitle">تصمیم یار هوشمند فرادید</span>
          </div>
        </div>
        <div className="ai-header-actions">
          <button
            className="ai-header-btn"
            onClick={handleToggleSessionsPanel}
            title="تاریخچه گفتگوها"
          >
            تاریخچه
          </button>
          <button
            className="ai-header-btn"
            onClick={handleStartNewChat}
            title="گفتگوی جدید"
          >
            جدید
          </button>
        </div>
      </div>

      <div className="ai-farm-select-row">
        <select
          className="ai-farm-select"
          value={selectedFarmId}
          onChange={(e) => setSelectedFarmId(e.target.value)}
        >
          <option value="">همه مزارع</option>
          {farms.map((farm) => (
            <option key={farm.id} value={farm.id}>
              {farm.name}
            </option>
          ))}
        </select>
      </div>

      <div className="ai-quick-buttons">
        <button
          className="ai-quick-btn"
          onClick={() => handleGenerateReport("گزارش وضعیت مزارع")}
          disabled={isLoading || reportLoading}
        >
          گزارش وضعیت مزارع
        </button>
        <button
          className="ai-quick-btn"
          onClick={() => handleGenerateReport("گزارش وضعیت گیاه")}
          disabled={isLoading || reportLoading}
        >
          گزارش وضعیت گیاه
        </button>
        <button
          className="ai-quick-btn"
          onClick={() => handleGenerateReport("گزارش وضعیت خاک")}
          disabled={isLoading || reportLoading}
        >
          گزارش وضعیت خاک
        </button>
      </div>

      {sessionsPanelOpen && (
        <div className={`ai-sessions-panel ${sessionsPanelOpen ? "open" : ""}`}>
          <div className="ai-sessions-header">
            <span className="ai-sessions-title">گفتگوهای قبلی</span>
            <button
              className="ai-sessions-close"
              onClick={() => setSessionsPanelOpen(false)}
              title="بستن"
            >
              ×
            </button>
          </div>
          <div className="ai-sessions-list">
            {sessionsLoading ? (
              <div className="ai-sessions-empty">در حال بارگذاری…</div>
            ) : sessions.length === 0 ? (
              <div className="ai-sessions-empty">
                هنوز گفتگویی ثبت نشده است.
              </div>
            ) : (
              sessions.map((s) => (
                <div
                  key={s.id}
                  className={`ai-session-item ${s.id === currentSessionId ? "active" : ""}`}
                  onClick={() => handleLoadSession(s.id)}
                >
                  <div className="ai-session-title">
                    {s.title || "گفتگوی جدید"}
                  </div>
                  <div className="ai-session-meta">
                    {formatDateFa(s.updated_at)}
                  </div>
                  <button
                    className="ai-session-delete"
                    onClick={(e) => handleDeleteSession(e, s.id)}
                    title="حذف"
                  >
                    حذف
                  </button>
                </div>
              ))
            )}
          </div>
          <div className="ai-sessions-footer">
            <button className="ai-new-chat-btn" onClick={handleStartNewChat}>
              گفتگوی جدید
            </button>
          </div>
        </div>
      )}

      <div className="ai-chat-scroll-container">
        {!sessionsPanelOpen && (
          <div className="ai-chat-messages">
            {chatHistory.length === 0 && (
              <div className="ai-welcome-msg">
                <p className="ai-welcome-text">
                  سلام! از من درباره مزارع، شاخص‌های ماهواره‌ای، وضعیت آبیاری،
                  منابع آبی یا توصیه‌های مدیریتی بپرسید.
                </p>
                <p className="ai-welcome-hint">
                  برای دستورات نقشه، گزینه <b>حالت نقشه</b> را فعال کنید.
                </p>
              </div>
            )}
            {chatHistory.map((msg, idx) => (
              <div
                key={idx}
                className={`ai-msg ${msg.role} ${msg.loading ? "loading" : ""} ${msg.isError ? "ai-msg-error" : ""}`}
              >
                {msg.loading ? (
                  <span>
                    در حال پردازش
                    <span className="dots" />
                  </span>
                ) : (
                  renderMessageContent(msg.content)
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {!sessionsPanelOpen && (
        <div className="ai-chat-input-area">
          <form className="ai-input-row" onSubmit={handleSendMessage}>
            <input
              ref={inputRef}
              type="text"
              className="ai-chat-input"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="پیام بنویسید…"
              autoComplete="off"
              disabled={isLoading}
            />
            <button
              type="submit"
              className="ai-send-btn"
              disabled={!inputValue.trim() || isLoading}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="2" y1="12" x2="20" y2="12" />
                <polyline points="6 6 2 12 6 18" />
              </svg>
            </button>
          </form>
          <div className="ai-map-mode-row">
            <label className="ai-map-mode-label">
              <span className="ai-toggle-track">
                <input
                  type="checkbox"
                  checked={mapMode}
                  onChange={(e) => setMapMode(e.target.checked)}
                />
                <span className="ai-toggle-thumb" />
              </span>
              حالت نقشه
            </label>
            <span className="ai-map-mode-hint">
              دستورات روی نقشه اعمال می‌شود
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiPanel;
